import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { LoadingState } from "./LoadingState";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  sortable?: boolean;
  sortKey?: string;
  width?: string | number;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  /** The query's error. With no rows, replaces the empty state; with rows, shows a banner. */
  error?: unknown;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  expandable?: (row: T) => ReactNode;
  pageSize?: number;
  totalRows?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  sortKey?: string;
  sortDir?: "asc" | "desc";
  onSortChange?: (key: string, dir: "asc" | "desc") => void;
  className?: string;
  dense?: boolean;
}

// Clicks on links, buttons and inputs inside a row belong to that control,
// not to the row's own click handler.
function isFromInteractiveChild(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("a,button,input,select,textarea,[role=menuitem]");
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function DataTable<T>({
  columns,
  rows,
  loading,
  error,
  onRetry,
  emptyTitle,
  emptyDescription,
  rowKey,
  onRowClick,
  expandable,
  pageSize = 50,
  totalRows,
  page = 0,
  onPageChange,
  sortKey,
  sortDir,
  onSortChange,
  className,
  dense,
}: DataTableProps<T>) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set());
  const hasError = error != null && error !== false;

  const toggleExpand = (key: string | number) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const total = totalRows ?? rows.length;
  const start = page * pageSize;
  const end = Math.min(start + pageSize, total);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const colSpan = columns.length + (expandable ? 1 : 0);

  const handleHeaderClick = (col: Column<T>) => {
    if (!col.sortable || !onSortChange) return;
    const key = col.sortKey ?? col.key;
    if (sortKey === key) {
      onSortChange(key, sortDir === "asc" ? "desc" : "asc");
    } else {
      onSortChange(key, "desc");
    }
  };

  const headerCells = useMemo(
    () =>
      columns.map((col) => {
        const key = col.sortKey ?? col.key;
        const isSorted = sortKey === key;
        const canSort = !!col.sortable && !!onSortChange;
        const icon = col.sortable ? (
          <span className="text-muted-foreground" aria-hidden="true">
            {isSorted ? (
              sortDir === "asc" ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )
            ) : (
              <ArrowUpDown className="h-3 w-3 opacity-50" />
            )}
          </span>
        ) : null;
        return (
          <TableHead
            key={col.key}
            className={cn(canSort && "select-none", col.className)}
            style={col.width != null ? { width: col.width } : undefined}
            aria-sort={canSort ? (isSorted ? (sortDir === "asc" ? "ascending" : "descending") : "none") : undefined}
          >
            {canSort ? (
              <button
                type="button"
                onClick={() => handleHeaderClick(col)}
                className="inline-flex items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {col.header}
                {icon}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1">
                {col.header}
                {icon}
              </span>
            )}
          </TableHead>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, sortKey, sortDir, onSortChange]
  );

  return (
    <div className={cn("flex flex-col rounded-lg border border-border/60 bg-card/40", className)}>
      {hasError && rows.length > 0 && !loading && (
        <div role="alert" className="flex items-center justify-between gap-3 border-b border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <span className="min-w-0 truncate">Refresh failed: {errorMessage(error)} · showing the last loaded rows</span>
          {onRetry && (
            <Button size="sm" variant="outline" className="h-7" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            {expandable && (
              <TableHead style={{ width: 30 }}>
                <span className="sr-only">Details</span>
              </TableHead>
            )}
            {headerCells}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <tr>
              <td colSpan={colSpan}>
                <LoadingState />
              </td>
            </tr>
          ) : hasError && rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan}>
                <ErrorState error={error} onRetry={onRetry} />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan}>
                <EmptyState title={emptyTitle ?? "No rows"} description={emptyDescription} />
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row);
              const expanded = expandedKeys.has(key);
              const detailId = `dt-${String(key)}-detail`;
              return (
                <Fragment key={String(key)}>
                  <TableRow
                    onClick={(e) => {
                      if (isFromInteractiveChild(e.target)) return;
                      if (expandable) toggleExpand(key);
                      else onRowClick?.(row);
                    }}
                    className={cn(
                      (expandable || onRowClick) && "cursor-pointer",
                      dense && "[&_td]:py-1.5"
                    )}
                    data-state={expanded ? "selected" : undefined}
                  >
                    {expandable && (
                      <TableCell className="text-muted-foreground">
                        <button
                          type="button"
                          aria-expanded={expanded}
                          aria-controls={expanded ? detailId : undefined}
                          aria-label={expanded ? "Hide details" : "Show details"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(key);
                          }}
                          className="grid h-6 w-6 place-items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {expanded ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                        </button>
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.key} className={col.className}>
                        {col.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {expandable && expanded && (
                    <TableRow className="bg-background/40 hover:bg-background/40">
                      <TableCell colSpan={columns.length + 1} className="p-0">
                        <div id={detailId} className="border-t border-border/40 px-4 py-3">{expandable(row)}</div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
      {onPageChange && (
        <div className="flex items-center justify-between gap-3 border-t border-border/40 px-3 py-2 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {hasError && rows.length === 0 ? "—" : total === 0 ? "0 rows" : `${start + 1}–${end} of ${total.toLocaleString()}`}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={page <= 0}
              onClick={() => onPageChange(Math.max(0, page - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
              Prev
            </Button>
            <span className="px-2 tabular-nums">
              Page {page + 1} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={end >= total}
              onClick={() => onPageChange(page + 1)}
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
